import { differenceInCalendarDays } from '@cobliteam/utils/date';

export type CompletionBeaconVariant = 'default' | 'success' | 'goal' | 'danger' | 'alert';

export type CompletionBeaconReason = 'no_show' | 'delivered_unscheduled_long' | 'stalled_long' | 'fleet_changed_with_pending' | 'delivered_unscheduled_short' | 'stalled_medium';

export type CompletionBeaconStatus = {
  variant: CompletionBeaconVariant;
  reason: CompletionBeaconReason | null;
};

export type GetCompletionBeaconVariantInput = {
  vehicleInstalledCount: number;
  fleetSize: number;
  upcomingFleetSize?: number | null;
  goalPct: number;
  shippingDate?: Date | null;
  deliveredTimeOfArrival?: Date | null;
  installationScheduledAt?: Date | null;
  daysWithoutMovement?: number | null;
  now?: Date;
};

const daysBetween = (from: Date | null | undefined, to: Date): number | null => {
  if (!from) return null;
  return differenceInCalendarDays(to, from);
};

export const getCompletionBeaconVariant = ({
  vehicleInstalledCount,
  fleetSize,
  upcomingFleetSize,
  goalPct,
  shippingDate,
  deliveredTimeOfArrival,
  installationScheduledAt,
  daysWithoutMovement,
  now = new Date(),
}: GetCompletionBeaconVariantInput): CompletionBeaconStatus | null => {
  if (!shippingDate) return null;

  if (fleetSize > 0 && vehicleInstalledCount >= fleetSize) return { variant: 'success', reason: null };

  const daysSinceDelivered = daysBetween(deliveredTimeOfArrival, now);
  const daysSinceScheduled = daysBetween(installationScheduledAt, now);
  const stalled = daysWithoutMovement ?? 0;
  const hasScheduled = installationScheduledAt != null;
  const hasDelivered = deliveredTimeOfArrival != null;

  const noShow = hasScheduled && daysSinceScheduled != null && daysSinceScheduled > 0;
  const deliveredButUnscheduled = hasDelivered && !hasScheduled;

  if (noShow) return { variant: 'danger', reason: 'no_show' };
  if (deliveredButUnscheduled && (daysSinceDelivered ?? 0) >= 15) return { variant: 'danger', reason: 'delivered_unscheduled_long' };
  if (stalled >= 30) return { variant: 'danger', reason: 'stalled_long' };

  const fleetSizeChanged = upcomingFleetSize != null && upcomingFleetSize !== fleetSize;

  if (fleetSizeChanged && deliveredButUnscheduled && (daysSinceDelivered ?? 0) >= 5) return { variant: 'alert', reason: 'fleet_changed_with_pending' };
  if (deliveredButUnscheduled && (daysSinceDelivered ?? 0) >= 3 && (daysSinceDelivered ?? 0) <= 9) return { variant: 'alert', reason: 'delivered_unscheduled_short' };
  if (stalled >= 20 && stalled < 30) return { variant: 'alert', reason: 'stalled_medium' };

  const installedPct = fleetSize > 0 ? (vehicleInstalledCount / fleetSize) * 100 : 0;
  if (installedPct >= goalPct) return { variant: 'goal', reason: null };

  return { variant: 'default', reason: null };
};
